import { lazy, Suspense, useState } from "react";

import { get2DImage, get3DImage } from "../../utils/common";

/**
 * Virtual Try-On dispatcher.
 *
 * Which experience ships is decided per OPENING of the modal, from the product
 * being tried on:
 *
 *   is3DVTO (or product.is_3D_VTO) true  + a glb  -> 3D try-on renderer
 *   otherwise                                      -> classic 2D try-on
 *
 * If a page does not pass the flag at all, the mode is inferred from which
 * assets the product has (only a glb -> 3D, only a 2D image -> 2D).
 *
 * The mode is not frozen for the lifetime of the modal. Every similar-product
 * card reports its product back through `onSelectSimilarProduct`; if that
 * product's VTO mode differs from the one currently open, the dispatcher swaps
 * to the other implementation and re-initialises it with the newly selected
 * product (2D image / glb model), so switching 2D -> 3D and 3D -> 2D works in
 * both directions.
 *
 * Opening the modal for a different product (or closing / re-opening it)
 * starts from scratch: the mode is re-derived and the try-on component is
 * remounted. Pages that keep this component mounted (All Products) therefore
 * never inherit the mode, frame or filters of the previous product.
 *
 * Each implementation is loaded lazily so that only the selected variant's
 * JavaScript and stylesheet are pulled into the page. The two try-on
 * implementations share a number of CSS class names, so keeping them in
 * separate async chunks prevents their styles from clashing.
 */
const VirtualTryOn3D = lazy(() => import("./Virtual_try_on_3d"));
const VirtualTryOn2D = lazy(() => import("./Virtual_try_on_2d"));

const get2DImageUrl = (images) => {
  const image = get2DImage(images);
  return image && image !== "NA"
    ? process.env.REACT_APP_IMAGE_URL + image
    : "";
};

const get3DImageUrl = (images) => {
  const url = get3DImage(images);
  return url && url !== "NA" ? url : "";
};

/** true / false, or null when the API did not provide a flag at all. */
const toFlag = (value) =>
  value === undefined || value === null ? null : Boolean(value);

/**
 * The decision itself.
 *
 * The `is_3D_VTO` flag is the product's declared type and mirrors how its own
 * PDP opens the try-on, so it wins: flag=true -> 3D, flag=false -> 2D
 * (regardless of extra assets). A product that declares 3D but ships no glb
 * degrades to 2D when a 2D image exists. Only when the flag is missing do we
 * infer the mode from the available assets, finally falling back to the
 * currently open mode.
 */
const decideIs3D = ({ flag, has3D, has2D, fallback }) => {
  if (flag === true && has3D) return true;
  if (flag === false) return false;
  if (flag === true) return has2D ? false : fallback;
  if (has3D && !has2D) return true;
  if (has2D && !has3D) return false;
  return fallback;
};

/** Mode for a similar product picked inside the modal. */
const resolveIs3D = (product, fallback) =>
  decideIs3D({
    flag:
      typeof product?.is_3D_VTO === "boolean" ? product.is_3D_VTO : null,
    has3D: !!get3DImageUrl(product?.Product_Images),
    has2D: !!get2DImageUrl(product?.Product_Images),
    fallback,
  });

export default function VirtualTryOn({
  isOpen,
  is3DVTO,
  productImages,
  glbModal,
  size,
  total_measurements,
  product, // the product the try-on was opened for (Details / Buy Now act on it)
  variant, // the PDP's selected variant (belongs to `product` only)
  ...props
}) {
  // What the page opened the modal for. Pages differ in what they pass, so
  // fall back to the product object itself.
  const baseImages = productImages ?? product?.Product_Images;
  const baseGlb = glbModal || get3DImageUrl(baseImages);
  const baseFlag = toFlag(is3DVTO ?? product?.is_3D_VTO);

  const initialIs3D = () =>
    decideIs3D({
      flag: baseFlag,
      has3D: !!baseGlb,
      has2D: !!get2DImageUrl(baseImages),
      fallback: false,
    });

  // One key per OPENING: it changes when the modal opens / closes or when it
  // is opened for a different product. Picking a similar product inside the
  // modal does not change it.
  const sessionKey = isOpen
    ? `${product?.id ?? ""}|${baseGlb}|${String(baseFlag)}`
    : "closed";

  // mode + the similar product that caused the last mode switch
  // (null = the original product props are still in use)
  const [session, setSession] = useState(() => ({
    key: sessionKey,
    is3D: initialIs3D(),
    switched: null,
  }));

  // Re-derive during render (not in an effect) so the wrong component is
  // never mounted for a frame — mounting one starts the camera.
  if (session.key !== sessionKey) {
    setSession({ key: sessionKey, is3D: initialIs3D(), switched: null });
  }

  const { is3D, switched: switchedProduct } = session;

  const handleSelectSimilarProduct = (selected) => {
    if (!selected) return;

    const nextIs3D = resolveIs3D(selected, is3D);

    if (nextIs3D === is3D) return;

    setSession((prev) => ({ ...prev, is3D: nextIs3D, switched: selected }));
  };

  // Initial product data for the component that is about to mount after a
  // mode switch: each variant only consumes the asset it renders (2D uses
  // `productImages`, 3D uses `glbModal`), so both are derived from the
  // switched product.
  const activeProductImages = switchedProduct
    ? switchedProduct.Product_Images ?? baseImages
    : baseImages;
  const activeGlb = switchedProduct
    ? get3DImageUrl(switchedProduct.Product_Images) || baseGlb
    : baseGlb;
  const activeSize = switchedProduct?.size ?? size ?? product?.size;
  const activeMeasurements =
    switchedProduct?.total_measurements ??
    total_measurements ??
    product?.total_measurements;
  const activeProduct = switchedProduct || product;

  const VirtualTryOnComponent = is3D ? VirtualTryOn3D : VirtualTryOn2D;

  return (
    <Suspense fallback={null}>
      <VirtualTryOnComponent
        key={sessionKey}
        {...props}
        isOpen={isOpen}
        productImages={activeProductImages}
        glbModal={activeGlb}
        size={activeSize}
        total_measurements={activeMeasurements}
        product={activeProduct}
        variant={switchedProduct ? undefined : variant}
        onSelectSimilarProduct={handleSelectSimilarProduct}
      />
    </Suspense>
  );
}